import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useSession } from "../auth.jsx";
import { Eyebrow, TierStamp, SlaClock } from "../components/Bits.jsx";

const STATE_LABEL = {
  queued: "Awaiting review",
  l2_drafted: "Draft ready to audit",
  in_review: "In review",
  returned: "Returned — L1 failed",
  released: "Released",
};

export default function Queue() {
  const s = useSession();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [err, setErr] = useState(null);

  useEffect(() => {
    const states =
      filter === "open" ? ["queued", "l2_drafted", "in_review"]
      : filter === "returned" ? ["returned"]
      : ["released"];

    // Masters see their cluster only. Directors and leads see the whole cohort.
    const base = collection(db, "submissions");
    const clauses = [where("state", "in", states)];
    if (s.role === "master" && s.profile?.clusterId) clauses.push(where("clusterId", "==", s.profile.clusterId));
    else if (s.profile?.cohortId) clauses.push(where("cohortId", "==", s.profile.cohortId));

    const q = query(base, ...clauses, orderBy("slaDueAt", "asc"), limit(100));
    return onSnapshot(q, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setErr(null);
    }, (e) => setErr(e.message));
  }, [filter, s.role, s.profile?.clusterId, s.profile?.cohortId]);

  const overdue = rows.filter((r) => r.slaDueAt?.toMillis?.() < Date.now()).length;

  return (
    <div>
      <div className="row between wrapflex" style={{ marginBottom: 12 }}>
        <div>
          <Eyebrow>Review queue</Eyebrow>
          <h1 style={{ fontSize: 24, marginTop: 4 }}>Oldest first, because the SLA is a promise</h1>
        </div>
        <div className="row">
          {["open", "returned", "released"].map((f) => (
            <button key={f} className={`btn ${filter === f ? "" : "ghost"}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
      </div>

      {overdue > 0 && (
        <div className="warn" style={{ marginBottom: 14 }}>
          <strong className="fail">{overdue} past the 72-hour feedback SLA.</strong>{" "}
          Clear these before starting anything new. If the backlog is structural rather than personal,
          say so in Friday's exception report — the fix is another Studio Master, not a longer week.
        </div>
      )}

      {err && (
        <div className="warn" style={{ marginBottom: 14 }}>
          Could not load the queue: {err.split(/(https:\/\/\S+)/g).map((part, i) =>
            part.startsWith("https://")
              ? <a key={i} href={part} target="_blank" rel="noreferrer">create the missing index (opens Firebase pre-filled)</a>
              : part
          )}
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Brief</th><th>Resident</th><th>Pod</th><th>Tier</th><th>State</th><th>L1</th><th>SLA</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.briefId}</td>
              <td>{r.residentName ?? r.residentUid?.slice(0, 6)}</td>
              <td className="mono" style={{ fontSize: 12 }}>{r.podId ?? "—"}</td>
              <td><TierStamp tier={r.tier} /></td>
              <td>{STATE_LABEL[r.state] ?? r.state}</td>
              <td>
                {r.l1?.failedHard?.length ? <span className="fail">{r.l1.failedHard.length} hard</span>
                  : r.l1?.failedSoft?.length ? <span style={{ color: "var(--bronze)" }}>{r.l1.failedSoft.length} soft</span>
                  : r.l1 ? <span className="pass">clean</span> : "—"}
              </td>
              <td><SlaClock dueAt={r.slaDueAt} /></td>
              <td><Link to={`/mark/${r.id}`}>Open</Link></td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={8} style={{ color: "var(--graphite)", padding: "18px 8px" }}>
              Nothing here. If that is a surprise, check your cluster assignment with your Studio Director.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
